import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ListVerificationsScreen.module.scss';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE, MAX_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import Logger from 'common/services/Logger';
import { useToasts } from 'react-toast-notifications';
import Loading from 'common/services/Loading';
import { CarVerificationListItemDto } from 'api/carVerification/models/CarVerificationDto';
import { CarVerificationSearchCriteria } from 'api/carVerification/models/CarVerificationSearchCriteria';
import CarVerificationService from 'api/carVerification/CarVerificationService';
import { VerificationCounterListDto } from 'api/carVerification/models/VerificationDto';
import VerificationListTable from '../verificationListTable/VerificationListTable';
import InfoCard from 'common/components/infoCard/InfoCard';
import GaugeIcon from 'assets/svg/gauge-white.svg';
import HourGlassIcon from 'assets/svg/hour-glass_white.svg';
import CalendarIcon from 'assets/svg/desktop_i_calendar.svg';
import AlertIcon from 'assets/svg/i_alertas_white.svg';
import colors from 'styles/export/colors.module.scss';
import useBidirectionalScroll from 'common/hooks/useBidirectionalScroll';
import { useParams } from 'react-router-dom';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import ReactTooltip from 'react-tooltip';
import { Filters } from '../../components/list-filters/VerificationFiltersScreen';
import { VehicleDto } from 'api/vehicles/models/VehicleDto';
import Badge from 'common/components/badge/Badge';
import { InterventionTypeEnum } from 'api/carVerification/enums/InterventionTypeEnum';
import { getInterventionTypeStatusColor } from 'common/utils/VerificationsUtils';

const INFO_CARD_COLORS = [
    colors.counterTeal1,
    colors.counterTeal2,
    colors.counterBlue1,
    colors.counterBlue2,
];

export type Props = {
    criteria?: CarVerificationSearchCriteria;
    editable: boolean;
    onClickItem: (item: CarVerificationListItemDto, event: any) => void;
    onVerificationsUpdated?: () => void | Promise<void>;
    vehicle?: VehicleDto;
    exportRef?: React.MutableRefObject<{ exportExcel: () => Promise<void> } | null>;
};

const ListVerificationsScreen = ({ criteria: _criteria, editable, onClickItem, onVerificationsUpdated, vehicle, exportRef }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const { vehicleId } = useParams<{ vehicleId: string }>();

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasCountersPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['CHARGES_TOTAL_READ']);

    const FILTERS_CACHE_KEY = 'vehicle-verifications-filters-' + (vehicleId ? 1 : 0);

    const [verifications, setVerifications] = useState<CarVerificationListItemDto[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [counters, setCounters] = useState<VerificationCounterListDto>();

    const bidirectionalScroll = useBidirectionalScroll({
        cacheKey: FILTERS_CACHE_KEY,
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
    });

    const {
        loadedPageRange,
        isLoadingPrevious,
        scrollToRowId: scrollToItemId,
        shouldLoadAdjacentPages,
        setLoadedPageRange,
        setIsLoadingPrevious,
        resetScrollState,
        calculatePageFromIndex,
        prepareLoadPrevious,
        adjustScrollAfterPrepend,
        finishRestoration,
        initiateRestoration,
        getHasMore,
        getHasMorePrevious,
        getOnLoadMore,
        getOnLoadPrevious,
    } = bidirectionalScroll;

    const [criteria, setCriteria] = useState<CarVerificationSearchCriteria>(_criteria || {
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderColumn: 'v.verification_date',
        orderBy: 'desc',
        vehicleId: vehicleId,
    });

    const [filters, setFilters] = useState<Filters>({});
    const isFirstLoad = useRef<boolean>(true);
    const previousCriteria = useRef<CarVerificationSearchCriteria>(criteria);
    const countersLoaded = useRef<boolean>(false);
    const [reRenderSearch, setReRenderSearch] = useState<number>(0);

    const getData = async (isLoadMore = false, shouldUpdateCounters = false) => {
        try {
            if (!isLoadMore) {
                Loading.show();
            } else {
                setIsLoadingMore(true);
            }

            const crit = { ...criteria, vehicleId: vehicleId ?? criteria.vehicleId };

            saveOrdernationFilters();

            const res = await CarVerificationService.getList(crit);
            setTotalItems(res.totalItems);

            if (isLoadMore) {
                setVerifications(prev => [...prev, ...res.items]);
                setLoadedPageRange(prev => ({ ...prev, max: criteria.page }));
            } else {
                setVerifications(res.items);
            }

            if (shouldUpdateCounters && hasCountersPolicy) {
                await getCounters(crit);
                countersLoaded.current = true;
            }

            ReactTooltip.rebuild();
            Loading.hide();
            setIsLoadingMore(false);

            if (shouldLoadAdjacentPages.current) {
                shouldLoadAdjacentPages.current = false;
                const currentPage = criteria.page;
                const totalPages = Math.ceil(res.totalItems / (criteria.itemsPerPage || DEFAULT_PAGINATION_ITEMS_PER_PAGE));

                if (currentPage > 1) {
                    try {
                        const prevCriteria = { ...crit, page: currentPage - 1 };
                        const prevResult = await CarVerificationService.getList(prevCriteria);
                        setVerifications(prev => [...prevResult.items, ...prev]);
                        setLoadedPageRange(prev => ({ ...prev, min: currentPage - 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.VERIFICATIONS, `Couldn't load previous page`, e);
                    }
                }

                if (currentPage < totalPages) {
                    try {
                        const nextCriteria = { ...crit, page: currentPage + 1 };
                        const nextResult = await CarVerificationService.getList(nextCriteria);
                        setVerifications(prev => [...prev, ...nextResult.items]);
                        setLoadedPageRange(prev => ({ ...prev, max: currentPage + 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.VERIFICATIONS, `Couldn't load next page`, e);
                    }
                }

                finishRestoration();
            }
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.VERIFICATIONS, `Couldn't get verifications list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            Loading.hide();
            setIsLoadingMore(false);
            resetScrollState();
        }
    };

    const getCounters = async (crit: CarVerificationSearchCriteria) => {
        try {
            const countersDB = await CarVerificationService.getCountersList(crit);
            setCounters(countersDB);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.VERIFICATIONS, `Couldn't get verifications counters`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
    };

    const formatTotalMinutes = (time: number) => {
        const hours = Math.floor(time / 60);
        const minutes = time % 60;
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}  h`;
    };

    const renderCounterBoxes = () => {
        return (
            <div className={styles.rowCounters}>
                {counters && (
                    <InfoCard
                        label={t('car_verification.list.total_kms') as string}
                        value={counters.totalKms ?? 0}
                        icon={GaugeIcon}
                        backgroundColor={INFO_CARD_COLORS[0]}
                        isKm
                    />
                )}
                {counters && (
                    <InfoCard
                        label={t('car_verification.list.time_in_travel') as string}
                        value={formatTotalMinutes(counters.totalMinutes ?? 0)}
                        icon={HourGlassIcon}
                        backgroundColor={INFO_CARD_COLORS[1]}
                    />
                )}
                {counters && (
                    <InfoCard
                        label={t('car_verification.list.number_verifications') as string}
                        value={counters.numberVerifications ?? 0}
                        icon={CalendarIcon}
                        backgroundColor={INFO_CARD_COLORS[2]}
                    />
                )}
                {counters && (
                    <InfoCard
                        label={t('car_verification.list.annotations') as string}
                        value={
                            <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                                <Badge backgroundColor={getInterventionTypeStatusColor(InterventionTypeEnum.INTERVENTION_URGENT).color} size={'round'} outlineColor={getInterventionTypeStatusColor(InterventionTypeEnum.INTERVENTION_URGENT).color}>{counters.numberAnnotationsUrgent ?? 0}</Badge>
                                <Badge backgroundColor={getInterventionTypeStatusColor(InterventionTypeEnum.INFORMATION).color} size={'round'} outlineColor={getInterventionTypeStatusColor(InterventionTypeEnum.INFORMATION).color}>{counters.numberAnnotationsInformative ?? 0}</Badge>
                                <Badge backgroundColor={getInterventionTypeStatusColor(InterventionTypeEnum.INTERVENTION).color} size={'round'} outlineColor={getInterventionTypeStatusColor(InterventionTypeEnum.INTERVENTION).color}>{counters.numberAnnotationsIntervention ?? 0}</Badge>
                            </div>
                        }
                        icon={AlertIcon}
                        backgroundColor={INFO_CARD_COLORS[3]}
                        subtitle={`${t('car_verification.list.annotation_type.urgent')} | ${t('car_verification.list.annotation_type.informative')} | ${t('car_verification.list.annotation_type.intervention')}`}
                    />
                )}
            </div>
        );
    };

    const fetchAllForExport = useCallback(async () => {
        const result = await CarVerificationService.getList({
            ...criteria,
            page: 1,
            itemsPerPage: MAX_PAGINATION_ITEMS_PER_PAGE,
        });
        return result.items;
    }, [criteria]);

    const onTableFilter = (field: string, isFilterAsc: boolean) => {
        setVerifications([]);
        resetScrollState();
        setTotalItems(0);
        setCriteria(prev => ({ ...prev, page: 1, orderBy: (isFilterAsc ? 'asc' : 'desc'), orderColumn: field }));
    };

    const loadMore = useCallback(() => {
        if (isLoadingMore || verifications.length >= totalItems) return;
        const nextPage = loadedPageRange.max + 1;
        setCriteria(prev => ({ ...prev, page: nextPage }));
    }, [isLoadingMore, verifications.length, totalItems, loadedPageRange.max]);

    const loadPrevious = useCallback(async () => {
        if (isLoadingPrevious || loadedPageRange.min <= 1) return;

        setIsLoadingPrevious(true);
        const prevPage = loadedPageRange.min - 1;

        try {
            prepareLoadPrevious();

            const prevCriteria: CarVerificationSearchCriteria = {
                ...criteria,
                page: prevPage,
                itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
            };
            const result = await CarVerificationService.getList(prevCriteria);

            setVerifications(prev => [...result.items, ...prev]);
            setLoadedPageRange(prev => ({ ...prev, min: prevPage }));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.VERIFICATIONS, `Couldn't get previous verification page`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        } finally {
            setIsLoadingPrevious(false);
        }
    }, [isLoadingPrevious, loadedPageRange.min, criteria, addToast, t]);

    useEffect(() => {
        if (!isLoadingPrevious) {
            adjustScrollAfterPrepend();
        }
    }, [verifications, isLoadingPrevious, adjustScrollAfterPrepend]);

    useEffect(() => {
        if (_criteria && !isFirstLoad.current) {
            onChangeFiltersFromParent(_criteria);
        }
    }, [_criteria]);

    useEffect(() => {
        if (isFirstLoad.current) {
            loadFilters();
        } else {
            const isLoadMore = criteria.page > loadedPageRange.max && verifications.length > 0;

            const prev = previousCriteria.current;
            const onlyOrderOrPageChanged =
                prev.startDate === criteria.startDate &&
                prev.endDate === criteria.endDate &&
                prev.vehicleId === criteria.vehicleId &&
                prev.driverResponsibleId === criteria.driverResponsibleId &&
                prev.type === criteria.type &&
                prev.interventionType === criteria.interventionType &&
                prev.tripStartDate === criteria.tripStartDate &&
                prev.tripEndDate === criteria.tripEndDate &&
                prev.allIn === criteria.allIn &&
                JSON.stringify(prev.localIds) === JSON.stringify(criteria.localIds) &&
                prev.localOperator === criteria.localOperator &&
                prev.segmentId === criteria.segmentId;

            const shouldLoadCounters = !countersLoaded.current || !onlyOrderOrPageChanged;
            getData(isLoadMore, shouldLoadCounters);
            previousCriteria.current = criteria;
        }
    }, [criteria]);

    const onChangeFiltersFromParent = (parentCriteria: CarVerificationSearchCriteria) => {
        setVerifications([]);
        resetScrollState();
        setTotalItems(0);
        countersLoaded.current = false;

        setFilters(prev => ({
            ...prev,
            vehicleId: parentCriteria.vehicleId,
            type: parentCriteria.type as any,
            driverResponsibleId: parentCriteria.driverResponsibleId,
            startDate: parentCriteria.startDate,
            endDate: parentCriteria.endDate,
            tripStartDate: parentCriteria.tripStartDate,
            tripEndDate: parentCriteria.tripEndDate,
            interventionType: parentCriteria.interventionType as any,
            orderBy: parentCriteria.orderBy,
            orderColumn: parentCriteria.orderColumn,
        }));

        setCriteria(prev => ({
            ...prev,
            ...parentCriteria,
            page: 1,
        }));
        setReRenderSearch(r => r + 1);
    };

    const loadFilters = () => {
        const cachedFilters = localStorage.getItem(FILTERS_CACHE_KEY);
        if (cachedFilters != null) {
            const f = JSON.parse(cachedFilters);

            if (f.scrollToItemId) {
                const startPage = initiateRestoration({
                    page: f.page || 1,
                    scrollToItemId: f.scrollToItemId,
                });

                const cleanedFilters = { ...f, scrollToItemId: undefined, page: 1 };
                localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(cleanedFilters));
                setFilters(cleanedFilters);

                setCriteria(prev => ({
                    ...prev,
                    vehicleId: _criteria?.vehicleId ?? f.vehicleId,
                    type: _criteria?.type ?? f.type,
                    driverResponsibleId: _criteria?.driverResponsibleId ?? f.driverResponsibleId,
                    startDate: _criteria?.startDate ?? f.startDate,
                    endDate: _criteria?.endDate ?? f.endDate,
                    tripStartDate: _criteria?.tripStartDate ?? f.tripStartDate,
                    tripEndDate: _criteria?.tripEndDate ?? f.tripEndDate,
                    interventionType: _criteria?.interventionType ?? f.interventionType,
                    localIds: _criteria?.localIds ?? f.localIds,
                    localOperator: _criteria?.localOperator ?? f.localOperator,
                    segmentId: _criteria?.segmentId ?? f.segmentId,
                    orderBy: f.orderBy ?? prev.orderBy,
                    orderColumn: f.orderColumn ?? prev.orderColumn,
                    page: startPage,
                    itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
                }));
                isFirstLoad.current = false;
                return;
            }

            f.scrollToItemId = undefined;
            f.page = 1;
            resetScrollState();
            localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(f));

            if (!_criteria) {
                setFilters(f);
                setCriteria(prev => ({
                    ...prev,
                    vehicleId: f.vehicleId,
                    type: f.type,
                    driverResponsibleId: f.driverResponsibleId,
                    startDate: f.startDate,
                    endDate: f.endDate,
                    tripStartDate: f.tripStartDate,
                    tripEndDate: f.tripEndDate,
                    interventionType: f.interventionType,
                    orderBy: f.orderBy,
                    orderColumn: f.orderColumn,
                    page: 1,
                }));
            } else {
                _criteria.page = 1;
                _criteria.orderBy = f.orderBy;
                _criteria.orderColumn = f.orderColumn;
                setCriteria(prev => ({ ...prev, ..._criteria, page: 1 }));
            }
        } else {
            getData(false, true);
        }

        isFirstLoad.current = false;
    };

    const onRowClick = (item: CarVerificationListItemDto, event: any) => {
        const itemIndex = verifications.findIndex(v => v.id === item.id);
        const actualPage = calculatePageFromIndex(itemIndex);

        const newFilters = { ...filters };
        newFilters.page = actualPage;
        (newFilters as any).scrollToItemId = item.id;
        newFilters.orderBy = criteria.orderBy;
        newFilters.orderColumn = criteria.orderColumn;
        saveFiltersOnCache(newFilters);

        onClickItem(item, event);
    };

    const saveOrdernationFilters = () => {
        const newFilters = { ...filters };
        newFilters.orderBy = criteria.orderBy;
        newFilters.orderColumn = criteria.orderColumn;
        saveFiltersOnCache(newFilters);
    };

    const saveFiltersOnCache = (f: Filters) => {
        setFilters(prev => ({ ...prev, ...f }));
        if ((f as any).scrollToItemId && f.page) {
            localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(f));
        } else {
            const { page, ...filtersWithoutPage } = f;
            const { scrollToItemId, ...rest } = filtersWithoutPage as any;
            localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(rest));
        }
    };

    const onDeleteFromList = () => {
        setVerifications([]);
        resetScrollState();
        countersLoaded.current = false;
        setCriteria(prev => ({ ...prev, page: 1 }));
    };

    return (
        <div className={styles.listWrapper}>
            {counters && (
                <div className={styles.countersSection}>{renderCounterBoxes()}</div>
            )}

            {verifications.length > 0 || totalItems === 0 ? (
                <div className={styles.listTblWrapper}>
                    <VerificationListTable
                        reRenderSearch={reRenderSearch}
                        items={verifications}
                        totalItems={totalItems}
                        isLoadingMore={isLoadingMore}
                        hasMore={getHasMore(verifications.length, totalItems)}
                        vehicleId={vehicleId}
                        editable={editable}
                        onLoadMore={getOnLoadMore(loadMore)}
                        onTableFilter={onTableFilter}
                        onClickItem={onRowClick}
                        onDeleteFromList={onDeleteFromList}
                        criteria={criteria}
                        scrollToRowId={scrollToItemId}
                        hasMorePrevious={getHasMorePrevious()}
                        isLoadingPrevious={isLoadingPrevious}
                        onLoadPrevious={getOnLoadPrevious(loadPrevious)}
                        vehicle={vehicle}
                        onVerificationsUpdated={onVerificationsUpdated}
                        onFetchAllForExport={fetchAllForExport}
                        exportRef={exportRef}
                    />
                </div>
            ) : null}
        </div>
    );
};

export default ListVerificationsScreen;
